import React, { useMemo } from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';
import { RootStateOrAny, useSelector } from 'react-redux';
import styled from 'styled-components';

import { desktop } from '../../styles';
import { useLDNewShoppingBagUX } from '../../utils/LD/useNewShoppingBagUX';
import {
  getInfluencerItemCountExcludingMembership,
  getItemCountExcludingMembership,
} from '../../utils/selectors';

function useShoppingBagIcon() {
  const router = useRouter();
  const { isV2 } = useLDNewShoppingBagUX();
  const shouldShowShoppingBagIcon = useMemo(() => {
    return isV2 && router.pathname.endsWith('order-information');
  }, [isV2, router.pathname]);
  return shouldShowShoppingBagIcon;
}

const ShoppingBagIconContainer = styled.a`
  position: absolute;
  right: 0;
  height: 24px;
  display: flex;
  gap: 4px;
  cursor: pointer;
  padding-right: ${({ theme }) => theme.spacing.tiny}px;
  ${desktop`
    padding-right: ${({ theme }) => theme.spacing.large}px;
  `}
`;

const ShoppingBagCountText = styled.p`
  color: ${({ theme }) => theme.colors.gunmetal};
  ${({ theme }) => theme.paragraph.variants.paragraph3Uppercase.textStyles};
`;

function ShoppingBagCount() {
  const cartItemCount = useSelector((state: RootStateOrAny) =>
    state.customer.isInfluencer
      ? getInfluencerItemCountExcludingMembership(state)
      : getItemCountExcludingMembership(state)
  );
  return cartItemCount > 0 ? (
    <ShoppingBagCountText>({cartItemCount})</ShoppingBagCountText>
  ) : null;
}

export function NavBarShoppingBagIcon() {
  const isShoppingBagButtonVisible = useShoppingBagIcon();
  if (!isShoppingBagButtonVisible) {
    return null;
  }
  return (
    <Link
      href="/cart"
      passHref
      data-autotag="checkout-back-to-cart"
      data-qa-automation="CheckoutBackToCart"
    >
      <ShoppingBagIconContainer>
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M10.3501 4.81233C10.7877 4.38367 11.3812 4.14286 12 4.14286C12.6188 4.14286 13.2123 4.38367 13.6499 4.81233C14.0875 5.24098 14.3333 5.82236 14.3333 6.42857L14.3333 7.57143H9.66667V6.42857C9.66667 5.82236 9.9125 5.24098 10.3501 4.81233ZM15.5 7.57143H19V19H5V7.57143H8.5V6.42857C8.5 5.51926 8.86875 4.64719 9.52513 4.0042C10.1815 3.36122 11.0717 3 12 3C12.9283 3 13.8185 3.36122 14.4749 4.00421C15.1313 4.64719 15.5 5.51926 15.5 6.42857L15.5 7.57143ZM14.3333 8.61039V11H15.5V8.61039H17.9231V17.961H6.07692V8.61039H8.5V11H9.66667V8.61039H14.3333Z"
            fill="black"
          />
        </svg>
        {isShoppingBagButtonVisible && <ShoppingBagCount />}
      </ShoppingBagIconContainer>
    </Link>
  );
}
